/**
 * Configuration Version Hooks — React Query hooks for configuration freshness
 * tracking.
 *
 * Polls the configuration version endpoint to detect when configuration changes
 * have been applied. The SIP engine polls the same version from the database;
 * this hook gives the web console visibility into that propagation.
 *
 * Polling: 10 s interval. React Query's default `refetchIntervalInBackground`
 * is false, so polling pauses when the window is unfocused.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ConfigurationAPI } from '@sbclite/core';
import { api } from '../services/api';

/** Full configuration version info returned by GET /api/configuration/version. */
type ConfigurationVersionInfo = ConfigurationAPI.GetVersionHistoryResponse['data'];

/** Response envelope for GET /api/configuration/version. */
interface VersionResponse {
  success: boolean;
  data: ConfigurationVersionInfo;
}

/** Response envelope for POST /api/configuration/reload. */
interface ReloadResponse {
  success: boolean;
  data: {
    version: number;
    message: string;
    reason: string;
  };
}

/** Centralized query key for configuration version. */
export const CONFIGURATION_VERSION_QUERY_KEY = ['configuration-version'] as const;

/**
 * Poll the current configuration version.
 *
 * Refetches every 10 seconds while the window is focused. Use this to derive
 * freshness state in the UI (e.g. last change type, propagation indicator).
 */
export function useConfigurationVersion() {
  return useQuery({
    queryKey: CONFIGURATION_VERSION_QUERY_KEY,
    queryFn: async (): Promise<ConfigurationVersionInfo> => {
      const response = await api.get<VersionResponse>('/api/configuration/version');
      return response.data.data;
    },
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}

/**
 * Trigger a full configuration reload (restarts the SIP engine gracefully).
 *
 * The mutation invalidates the version query on success so the banner
 * immediately reflects the new version.
 */
export function useTriggerFullReload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<ReloadResponse['data']> => {
      const response = await api.post<ReloadResponse>('/api/configuration/reload', { reason });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONFIGURATION_VERSION_QUERY_KEY });
    },
  });
}
